import React, {useCallback, useState} from "react"
import Row from "@amzn/meridian/row"
import Button from "@amzn/meridian/button"
import ModalExample from "@amzn/meridian/modal";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Currency from "../common/currency";
import ModalFooter from "@amzn/meridian/modal/modal-footer";
import Column from "@amzn/meridian/column";
import {getStationCode} from "../../network/NetworkUtil";
import {createRemittanceRequest} from '../remittance/network/RemittanceAPI';
import Alert from "@amzn/meridian/alert";
import Icon from "@amzn/meridian/icon";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import AlertModal from "./alertModal";
import Link from "@amzn/meridian/link";
import Card, {CardHeader} from "@amzn/meridian/card";
import Heading from "@amzn/meridian/heading";
import AddCoinDepositModal from "./addCoinDepositModal";
import Tooltip from "@amzn/meridian/tooltip";

/*
This component will render various section of Create Remittance Modal
 */
const SectionDetails = (props) => {
    return (
        <Box spacingInset="small none">
            <Row>
                <Text type="h200">{props.title}</Text></Row>
            <Row>
                <Text color="secondary" type="b200">{props.description}</Text>
            </Row>
        </Box>
    );
}

/**
 * This component will render Overview Section of Create Remittance modal to show expected and actual amount
 * @param amountValues
 * @param mposAmount
 * @returns {JSX.Element}
 * @constructor
 */
const OverviewSection = ({amountValues, mposAmount}) => {
    const {amount = '', variance = '', cashToBeSubmitted = ''} = amountValues;
    let mposLiabilityMessage = mposAmount.amount.value > 0 ? "Digital liability of " +
        Math.round(mposAmount.amount.value) + " will also be created" : "You don't have any Digital liability";

    return (<Box>
            <Row>
                <SectionDetails title="Overview" description="Prepare the bank deposits for cash amount collected manually at the station"/>
            </Row>

            <Box>
                <Row spacingInset="small none">
                    <Text color="secondary">
                        <Text><Icon tokens={infoKnockoutTokens}/> {mposLiabilityMessage}</Text>
                    </Text>
                </Row>
            </Box>

            <Row width="100%" widths={["grid-4", "grid-4","grid-4"]} spacingInset="xxsmall">
                <Tooltip position="right" title = "This is the amount all drivers should have collected based on the packages delivered" >
                    <Card>
                        <CardHeader>
                            <Heading level={4} type="b100">
                                <b>Expected Cash</b>
                            </Heading>
                        </CardHeader>
                        <Currency amount={amount}/>
                    </Card>
                </Tooltip>
                <Tooltip position="right" title = "This is the amount collected by the station" >
                    <Card>
                        <CardHeader>
                            <Heading level={4} type="b100">
                                <b>Actual Cash</b>
                            </Heading>
                        </CardHeader>
                        <Currency amount={cashToBeSubmitted}/>
                    </Card>
                </Tooltip>
                <Tooltip position="right" title = "This is Expected - Actual">
                        <Card>
                            <CardHeader>
                                <Heading level={4} type="b100">
                                    <b>Short/Excess</b>
                                </Heading>
                            </CardHeader>
                            <Currency isVariance={true} amount={variance}/>
                        </Card>
                </Tooltip>
            </Row>
        </Box>
    );
}

const CreateRemittanceModal = (props) => {
    const [alertBox, setAlertBox] = useState();
    const [alertModal, setAlertModal] = useState({show: false});
    const [openLink, setOpenLink] = useState(false);
    const onClickLink = useCallback(() => setOpenLink(true), [])
    const onCloseLink = useCallback(() => setOpenLink(false), [])
    const onClose = event => {
        setAlertBox()
        props.onClose(event);
    }

    const onAlertModalClose = event => {
        onClose(event);
        setAlertModal({show: false});
        window.location.reload();
    }
    const createRemittance = event => {
        return createRemittanceRequest({
            stationCode: getStationCode()
        }).then(response => {
            if (response.status === 200) {
                setAlertModal({
                    show: true,
                    type: "success",
                    title: "Success",
                    message: "Successfully submitted deposit"
                });
            } else {
                setAlertModal({show: true, type: "error", title: "Failure", message: "Failed to create deposit"});
            }
        }).catch(error => {
            setAlertBox({type: "error", message: "Failed to create deposit"});
        });
    }

    const {liabilitySummary,countryCode} = props;
    const overviewAmountValues = {
        amount: ((liabilitySummary.cashSummary || {}).expectedAmount || {}),
        variance: ((liabilitySummary.cashSummary || {}).shortExcessAmount || {}),
        cashToBeSubmitted: ((liabilitySummary.cashSummary || {}).actualAmount || {})
    };

    let hasLiability = (overviewAmountValues.cashToBeSubmitted.value > 0 || (liabilitySummary.mposSummary && liabilitySummary.mposSummary.amount.value > 0));
    return (
        <React.Fragment>
            {<React.Fragment>
                <ModalExample title="Prepare Deposit" open={props.value} scrollContainer="viewport"
                              width={535} closeLabel="Close" onClose={onClose}>
                    {alertBox &&
                    <Alert type={alertBox.type ? alertBox.type : "informational"}>{alertBox.message}</Alert>}
                    {hasLiability ? <Box>
                        <form>
                            <Column spacing="small">
                                <OverviewSection
                                    amountValues={overviewAmountValues}
                                    mposAmount={liabilitySummary.mposSummary}
                                />
                            </Column>
                        </form>
                    </Box> : <Alert type="informational">You don't have any liability to prepare deposit</Alert>
                    }
                    <ModalFooter>
                        <Row alignmentHorizontal="right" widths="fit">
                            <Button type="secondary" size="medium" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="primary" size="medium" disabled={!hasLiability}
                                    onClick={createRemittance}>
                                Prepare deposit
                            </Button>
                        </Row>
                        {countryCode==="AE" || countryCode==="SA" ? <Row spacingInset="medium" alignmentHorizontal="center" >
                            <Link type="secondary" to="#" onClick={onClickLink}>I need to prepare a deposit for coins and notes rejected by CDM</Link>

                            <AddCoinDepositModal
                                value={openLink}
                                onClose={onCloseLink}
                                currencyUnit={liabilitySummary.cashSummary.expectedAmount.unit}
                            />
                        </Row> : countryCode==="" ? <Row spacingInset="medium" alignmentHorizontal="center" >
                            Fetching Country code timed out. Refresh and retry
                        </Row> : ""}
                    </ModalFooter>
                </ModalExample>

                <AlertModal
                    show={alertModal.show}
                    title={alertModal.title}
                    type={alertModal.type}
                    message={alertModal.message}
                    onClose={onAlertModalClose}
                />
            </React.Fragment>}
        </React.Fragment>
    );
}

export default CreateRemittanceModal;



// WEBPACK FOOTER //
// ./src/component/modal/createRemittanceModal.js